/**
 * Anniversary Types
 *
 * Type definitions for family anniversaries and day counters.
 * Track important dates and display days since/until.
 *
 * @module features/family-calendar/types/anniversary.types
 */

// ============================================
// Core Anniversary Types
// ============================================

/**
 * Anniversary/Day Counter entity.
 * Tracks important family dates with countdown/countup display.
 */
export interface Anniversary {
  /** Unique identifier */
  readonly id: string;

  /** Anniversary name/title */
  readonly name: string;

  /** The date to track (ISO string, YYYY-MM-DD) */
  readonly date: string;

  /** Type of counter: anniversary (past) or countdown (future) */
  readonly type: AnniversaryType;

  /** Category for grouping and display */
  readonly category: AnniversaryCategoryRef;

  /** Optional description */
  readonly description?: string;

  /** Emoji icon for display */
  readonly emoji: string;

  /** Color theme for the card */
  readonly color: AnniversaryColorRef;

  /** Whether this is a recurring annual event */
  readonly isRecurring: boolean;

  /** Whether to show notification on anniversary */
  readonly notifyOnDay: boolean;

  /** Calendar this anniversary belongs to */
  readonly calendarId: string;

  /** Creator's member ID */
  readonly createdBy: string;

  /** Record creation timestamp */
  readonly createdAt: string;

  /** Record last update timestamp */
  readonly updatedAt: string;
}

// ============================================
// Enum Types
// ============================================

/**
 * Anniversary counter type
 * - 'anniversary': Count days since (past event)
 * - 'countdown': Count days until (future event)
 */
export type AnniversaryType = 'anniversary' | 'countdown';

/**
 * All anniversary types
 */
export const ALL_ANNIVERSARY_TYPES: readonly AnniversaryType[] = [
  'anniversary',
  'countdown',
] as const;

/**
 * Anniversary type labels (Bulgarian)
 */
export const ANNIVERSARY_TYPE_LABELS: Readonly<
  Record<AnniversaryType, string>
> = {
  anniversary: 'Годишнина',
  countdown: 'Обратно броене',
} as const;

/**
 * Anniversary categories
 */
export type AnniversaryCategory =
  | 'family'
  | 'birthday'
  | 'wedding'
  | 'school'
  | 'travel'
  | 'achievement'
  | 'other';

/**
 * An anniversary category reference. Either a seed {@link AnniversaryCategory}
 * id (rendered via {@link ANNIVERSARY_CATEGORY_CONFIG}) or any configurable
 * category id from the Categories store.
 */
export type AnniversaryCategoryRef = AnniversaryCategory | (string & {});

/**
 * All anniversary categories
 */
export const ALL_ANNIVERSARY_CATEGORIES: readonly AnniversaryCategory[] = [
  'family',
  'birthday',
  'wedding',
  'school',
  'travel',
  'achievement',
  'other',
] as const;

/**
 * Type guard for the seed {@link AnniversaryCategory} set (used when
 * persisting to the DB enum, which only accepts the seed values).
 */
export function isAnniversaryCategory(
  value: unknown
): value is AnniversaryCategory {
  return (
    typeof value === 'string' &&
    ALL_ANNIVERSARY_CATEGORIES.includes(value as AnniversaryCategory)
  );
}

/**
 * Anniversary category configuration
 */
export const ANNIVERSARY_CATEGORY_CONFIG: Readonly<
  Record<
    AnniversaryCategory,
    {
      readonly label: string;
      readonly emoji: string;
      readonly description: string;
    }
  >
> = {
  family: {
    label: 'Семейство',
    emoji: '👨‍👩‍👧‍👦',
    description: 'Семейни събития',
  },
  birthday: {
    label: 'Рожден ден',
    emoji: '🎂',
    description: 'Рождени дни',
  },
  wedding: {
    label: 'Сватба',
    emoji: '💍',
    description: 'Сватбени годишнини',
  },
  school: {
    label: 'Училище',
    emoji: '🎓',
    description: 'Учебни събития',
  },
  travel: {
    label: 'Пътуване',
    emoji: '✈️',
    description: 'Пътувания и ваканции',
  },
  achievement: {
    label: 'Постижение',
    emoji: '🏆',
    description: 'Важни постижения',
  },
  other: {
    label: 'Друго',
    emoji: '📌',
    description: 'Други важни дати',
  },
} as const;

/**
 * Available anniversary colors
 */
export type AnniversaryColor =
  | 'red'
  | 'orange'
  | 'yellow'
  | 'green'
  | 'blue'
  | 'purple'
  | 'pink';

/**
 * An anniversary colour reference. Either a seed {@link AnniversaryColor} id
 * (rendered via {@link ANNIVERSARY_COLOR_CONFIG}) or any configurable colour id
 * from the Colors store.
 */
export type AnniversaryColorRef = AnniversaryColor | (string & {});

/**
 * All anniversary colors
 */
export const ALL_ANNIVERSARY_COLORS: readonly AnniversaryColor[] = [
  'red',
  'orange',
  'yellow',
  'green',
  'blue',
  'purple',
  'pink',
] as const;

/**
 * Anniversary color configuration with theme-aware styling
 */
export const ANNIVERSARY_COLOR_CONFIG: Readonly<
  Record<
    AnniversaryColor,
    {
      readonly label: string;
      readonly bgClass: string;
      readonly textClass: string;
      readonly borderClass: string;
      readonly hoverClass: string;
    }
  >
> = {
  red: {
    label: 'Червено',
    bgClass: 'bg-red-100 dark:bg-red-900/30',
    textClass: 'text-red-700 dark:text-red-300',
    borderClass: 'border-red-300 dark:border-red-700',
    hoverClass: 'hover:bg-red-200 dark:hover:bg-red-900/50',
  },
  orange: {
    label: 'Оранжево',
    bgClass: 'bg-orange-100 dark:bg-orange-900/30',
    textClass: 'text-orange-700 dark:text-orange-300',
    borderClass: 'border-orange-300 dark:border-orange-700',
    hoverClass: 'hover:bg-orange-200 dark:hover:bg-orange-900/50',
  },
  yellow: {
    label: 'Жълто',
    bgClass: 'bg-yellow-100 dark:bg-yellow-900/30',
    textClass: 'text-yellow-700 dark:text-yellow-300',
    borderClass: 'border-yellow-300 dark:border-yellow-700',
    hoverClass: 'hover:bg-yellow-200 dark:hover:bg-yellow-900/50',
  },
  green: {
    label: 'Зелено',
    bgClass: 'bg-green-100 dark:bg-green-900/30',
    textClass: 'text-green-700 dark:text-green-300',
    borderClass: 'border-green-300 dark:border-green-700',
    hoverClass: 'hover:bg-green-200 dark:hover:bg-green-900/50',
  },
  blue: {
    label: 'Синьо',
    bgClass: 'bg-blue-100 dark:bg-blue-900/30',
    textClass: 'text-blue-700 dark:text-blue-300',
    borderClass: 'border-blue-300 dark:border-blue-700',
    hoverClass: 'hover:bg-blue-200 dark:hover:bg-blue-900/50',
  },
  purple: {
    label: 'Лилаво',
    bgClass: 'bg-purple-100 dark:bg-purple-900/30',
    textClass: 'text-purple-700 dark:text-purple-300',
    borderClass: 'border-purple-300 dark:border-purple-700',
    hoverClass: 'hover:bg-purple-200 dark:hover:bg-purple-900/50',
  },
  pink: {
    label: 'Розово',
    bgClass: 'bg-pink-100 dark:bg-pink-900/30',
    textClass: 'text-pink-700 dark:text-pink-300',
    borderClass: 'border-pink-300 dark:border-pink-700',
    hoverClass: 'hover:bg-pink-200 dark:hover:bg-pink-900/50',
  },
} as const;

// ============================================
// DTO Types
// ============================================

/**
 * Data for creating a new anniversary
 */
export interface CreateAnniversaryDTO {
  readonly name: string;
  readonly date: string;
  readonly type: AnniversaryType;
  readonly category: AnniversaryCategoryRef;
  readonly description?: string;
  readonly emoji?: string;
  readonly color?: AnniversaryColorRef;
  readonly isRecurring?: boolean;
  readonly notifyOnDay?: boolean;
  readonly calendarId: string;
  readonly createdBy: string;
}

/**
 * Data for updating an existing anniversary
 */
export interface UpdateAnniversaryDTO {
  readonly name?: string;
  readonly date?: string;
  readonly type?: AnniversaryType;
  readonly category?: AnniversaryCategoryRef;
  readonly description?: string;
  readonly emoji?: string;
  readonly color?: AnniversaryColorRef;
  readonly isRecurring?: boolean;
  readonly notifyOnDay?: boolean;
}

// ============================================
// Utility Types
// ============================================

/**
 * Anniversary with calculated day count
 */
export interface AnniversaryWithDays extends Anniversary {
  /** Days since/until the date (positive = past, negative = future) */
  readonly daysDiff: number;
  /** Absolute number of days */
  readonly daysCount: number;
  /** Human-readable label */
  readonly daysLabel: string;
  /** Next occurrence date (for recurring) */
  readonly nextOccurrence?: string;
  /** Years since the original date */
  readonly yearsSince: number;
}

/**
 * Default emoji options for quick selection
 */
export const ANNIVERSARY_EMOJI_OPTIONS: readonly string[] = [
  '❤️',
  '💍',
  '🎂',
  '🎉',
  '🎓',
  '✈️',
  '🏆',
  '⭐',
  '🌟',
  '👨‍👩‍👧‍👦',
  '🏠',
  '🎄',
  '🌸',
  '☀️',
  '🎁',
  '📅',
] as const;
